import { Box } from '@chakra-ui/react';
import { ColumnFiltersState, createColumnHelper, Row } from '@tanstack/react-table';
import React from 'react';
import { useImmer } from 'use-immer';

import { useGetViewsQuery, ViewOrderBy } from '@/API/views.api';
import { cellFactory } from '@/components/available-preview-list/aplUtils';
import { PersonnelViewMembershipAccessTabs, TableColumnIds } from '@/constants/enums';
import { ACCESS_DRAWER_TOP_POSITION } from '@/constants/ui';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { setPersonnelMembershipDrawerDraftAccess } from '@/store/slices/personnelMembershipDrawer.slice';
import { ViewLite } from '@/types/view.types';
import { tableColumnFilterFn } from '@/utils/filterFns';

import AvailablePreviewList from '../available-preview-list/AvailablePreviewList';

interface TableState {
  availableItemsColumnFilters: ColumnFiltersState;
}

const PersonnelViewAccess = (): React.JSX.Element => {
  const {
    isDepartmentListOpen,
    isLoading,
    personnelViewAccessDraft,
    personnelViewAccessOriginal,
    selectedDepartmentIds,
    selectedTabIndex,
    selectedTemplateIds,
  } = useAppSelector((state) => state.personnelMembershipDrawer);

  const dispatch = useAppDispatch();

  const [state, updateTableState] = useImmer<TableState>({
    availableItemsColumnFilters: [],
  });

  const { data: viewsData, isLoading: isViewsLoading } = useGetViewsQuery(
    {
      departmentList: selectedDepartmentIds,
      filter: '',
      isAscending: true,
      orderBy: ViewOrderBy.ViewName,
      templateList: [],
      viewIdsList: personnelViewAccessOriginal.viewIds,
    },
    {
      skip:
        selectedTabIndex !== PersonnelViewMembershipAccessTabs.ACCESS ||
        isLoading ||
        ((!selectedDepartmentIds.length || isDepartmentListOpen) && !personnelViewAccessOriginal.viewIds.length),
    },
  );

  const [availableItems, previewItems] = React.useMemo(() => {
    const available = new Map<number, ViewLite>();
    const preview = new Map<number, ViewLite>();

    const currentViewIds = personnelViewAccessDraft.viewIds;

    if (!viewsData) return [available, preview];

    for (const view of viewsData) {
      if (currentViewIds.includes(view.id)) {
        preview.set(view.id, view);

        continue;
      }

      available.set(view.id, view);
    }

    return [available, preview];
  }, [personnelViewAccessDraft.viewIds, viewsData]);

  // ToDo: Investigate possibly deduplicating this column definition logic
  const rowTemplateIdFilterFn = React.useCallback(
    (row: Row<ViewLite>, _: string, filterValue: number[]) => {
      if (!filterValue.length) return true;

      // eslint-disable-next-line react/prop-types
      const rowTemplateIds: number[] = row.getValue(TableColumnIds.templateIds);

      return selectedTemplateIds.some((templateId) => rowTemplateIds.includes(templateId));
    },
    [selectedTemplateIds],
  );

  const rowDepartmentIdFilterFn = React.useCallback(
    (row: Row<ViewLite>, _: string, filterValue: number[]) => {
      if (!filterValue.length) return false;

      // eslint-disable-next-line react/prop-types
      const rowDepartmentIds: number[] = row.getValue(TableColumnIds.departmentIds);

      return selectedDepartmentIds.some((departmentId) => rowDepartmentIds.includes(departmentId));
    },
    [selectedDepartmentIds],
  );

  const rowViewIdFilterFn = React.useCallback(
    (row: Row<ViewLite>, _: string, filterValue: number[]) => {
      if (!filterValue.length) return true;

      return personnelViewAccessDraft.viewIds.every((viewId) => viewId !== row.original.id);
    },
    [personnelViewAccessDraft.viewIds],
  );

  const availableColumnHelper = createColumnHelper<ViewLite>();
  const availableItemsColumns = [
    // eslint-disable-next-line react/prop-types
    availableColumnHelper.accessor((row) => row.name, {
      cell: (item) => cellFactory<ViewLite>({ cellContext: item, text: item.row.original.name }),
      enableSorting: true,
      filterFn: tableColumnFilterFn,
      header: () => <Box>Name</Box>,
      id: TableColumnIds.viewName,
      sortDescFirst: true,
    }),
    // eslint-disable-next-line react/prop-types
    availableColumnHelper.accessor((row) => row.id, {
      cell: undefined,
      enableHiding: true,
      filterFn: rowViewIdFilterFn,
      id: TableColumnIds.viewId,
    }),
    // eslint-disable-next-line react/prop-types
    availableColumnHelper.accessor((row) => row.templates, {
      cell: undefined,
      enableHiding: true,
      filterFn: rowTemplateIdFilterFn,
      header: undefined,
      id: TableColumnIds.templateIds,
    }),
    // eslint-disable-next-line react/prop-types
    availableColumnHelper.accessor((row) => row.departments, {
      cell: undefined,
      enableHiding: true,
      filterFn: rowDepartmentIdFilterFn,
      header: undefined,
      id: TableColumnIds.departmentIds,
    }),
  ];

  const previewColumnsHelper = createColumnHelper<ViewLite>();

  const previewColumns = [
    // eslint-disable-next-line react/prop-types
    previewColumnsHelper.accessor((row) => row.name, {
      cell: (item) => cellFactory<ViewLite>({ cellContext: item, text: item.row.original.name }),
      enableSorting: true,
      filterFn: tableColumnFilterFn,
      header: () => <Box>Name</Box>,
      id: TableColumnIds.viewName,
      sortDescFirst: true,
    }),
    // eslint-disable-next-line react/prop-types
    previewColumnsHelper.accessor((row) => row.id, {
      cell: undefined,
      enableHiding: true,
      id: TableColumnIds.viewId,
    }),
  ];

  const handleSelectedItemsChanged = React.useCallback(
    (items: ViewLite[]) => {
      const viewIds = items.map((item) => item.id);

      dispatch(setPersonnelMembershipDrawerDraftAccess(viewIds));
    },
    [dispatch],
  );

  React.useEffect(() => {
    updateTableState((draft) => {
      draft.availableItemsColumnFilters = [
        {
          id: TableColumnIds.departmentIds,
          value: selectedDepartmentIds,
        },
        {
          id: TableColumnIds.templateIds,
          value: selectedTemplateIds,
        },
        {
          id: TableColumnIds.viewId,
          value: personnelViewAccessDraft.viewIds,
        },
      ];
    });
  }, [updateTableState, selectedDepartmentIds, selectedTemplateIds, personnelViewAccessDraft.viewIds]);

  return (
    <AvailablePreviewList
      availableColumns={availableItemsColumns}
      availableColumnsVisibility={{ [TableColumnIds.viewId]: false }}
      availableItems={availableItems}
      availableItemsColumnFilters={state.availableItemsColumnFilters}
      availableItemsPrimaryColumnId={TableColumnIds.viewName}
      availableItemsTableLabel={'Available Views'}
      availableRowUniqueKey={TableColumnIds.id}
      isLoading={isViewsLoading}
      isOpen={selectedTabIndex === PersonnelViewMembershipAccessTabs.ACCESS}
      onItemsChanged={handleSelectedItemsChanged}
      previewColumns={previewColumns}
      previewColumnsVisibility={{ [TableColumnIds.viewId]: false }}
      previewItems={previewItems}
      previewItemsPrimaryColumnId={TableColumnIds.viewName}
      previewItemsTableLabel={`Selected Views`}
      previewRowUniqueKey={TableColumnIds.id}
      primaryAvailableColumnIndex={0}
      primaryPreviewColumnIndex={0}
      tableColumnIds={TableColumnIds}
      topPosition={ACCESS_DRAWER_TOP_POSITION}
    />
  );
};

export default PersonnelViewAccess;
